import { Component, OnInit, Input, ChangeDetectorRef } from '@angular/core'
import { UtilsService } from '@app/core/_services/utils.service'
import { filter, take, takeUntil } from 'rxjs/operators'
import {
    MeetingsBookingsGroupMeeting,
    MeetingsBookingsMeetingItem,
    MeetingsBookingsPairedMeeting,
    MeetingsParticipantInfo,
    MeetingsParticipantsMap,
    ConfirmedSessionsResponse,
} from '@app/core/_models'
import { Subject } from 'rxjs'
import moment from 'moment-timezone/builds/moment-timezone-with-data-10-year-range.min'
import { User } from '@app/core/_models'
import { UserService } from '@app/core/_services/user.service'
import { GroupInfo } from '@app/core/_models/rest_api'
import { AnalyticsService, SessionTileSource } from '@app/core/_services/analytics.service'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import {
    MAX_SET_TIMEOUT_VALUE,
    TimeUtilitiesService,
} from '@app/core/_services/time-utilities.service'
import { BookedSessionTileComponent } from '@app/routes/dashboard/components/booked-session-tile/booked-session-tile.component'
import { GroupMeetingTileComponent } from '@app/routes/dashboard/components/group-meeting-tile/group-meeting-tile.component'
import { EpochToTimePipe } from '@app/core/pipes/epoch-to-time.pipe'
import { SharedModule } from '@app/shared/shared.module'
import { ButtonModule } from 'primeng/button'
import { Router } from '@angular/router'
import { CalendarViewService } from '@app/routes/dashboard/services/calendar-view.service'
import { UpcomingSessionsService } from '@app/left-panel/services/upcoming-sessions.service'
import { ViewportService } from '@app/core/_services/viewport.service'
import { DialogService } from '@app/core/_services/dialog.service'
import { FeatureCheckService } from '@app/core/_services/feature-check.service'
import { MeetingsService } from '@app/core/_services/meetings.service'

@Component({
    selector: 'fm-upcoming-session-panel',
    templateUrl: './upcoming-session-panel.component.html',
    styleUrls: ['./upcoming-session-panel.component.scss'],
    standalone: true,
    imports: [
        BookedSessionTileComponent,
        GroupMeetingTileComponent,
        EpochToTimePipe,
        SharedModule,
        ButtonModule,
    ],
})
export class UpcomingSessionPanelComponent implements OnInit {
    public timeZone: string
    private ngUnsubscribe: Subject<any> = new Subject<any>()
    public currentTime: number
    public profileInfo: User
    public group: GroupInfo[] = []
    faPlus = faPlus

    private meetingsBookingsMeetings: MeetingsBookingsMeetingItem[] = []
    private meetingsParticipants: MeetingsParticipantsMap = {}

    public SessionTileSource = SessionTileSource

    public sessionToShow: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem = null
    public showTimer: boolean = false
    public sessionDateStr: string = null
    public isSessionOngoing: boolean = false

    public mobileShowUpcomingSession: boolean = false

    public isNewUserForAmbassadorProgram: boolean = false
    private hasRegisteredForSessionUpdates: boolean = false

    constructor(
        public viewportService: ViewportService,
        private calendarViewSerivce: CalendarViewService,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService,
        private meetingsService: MeetingsService,
        private upcomingSessionsService: UpcomingSessionsService,
        private timeUtils: TimeUtilitiesService,
        private analyticsService: AnalyticsService,
        private dialogService: DialogService,
        private featureCheckService: FeatureCheckService,
    ) {}

    ngOnInit() {
        this.userService.currentUser.pipe(takeUntil(this.ngUnsubscribe)).subscribe((user: User) => {
            this.profileInfo = { ...user }
            this.timeZone = user.timeZone
            this.isNewUserForAmbassadorProgram =
                this.featureCheckService.isNewUserForAmbassadorProgram(user)

            if (!this.hasRegisteredForSessionUpdates) {
                this.hasRegisteredForSessionUpdates = true
                this.registerForSessionUpdates()
            }
        })

        this.timeUtils
            .onMinuteStart()
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(() => {
                this.processSessions()
            })
    }

    private resetUpcomingSessionInfo(): void {
        this.sessionToShow = null
        this.showTimer = false
        this.sessionDateStr = null
        this.isSessionOngoing = false
        this.mobileShowUpcomingSession = false
    }

    private updateMobileUpcomingSessionBanner(): void {
        // For mobile
        // Only show session from 10 minutes prior to end of session
        const withinTenMinutesofSession = this.utilsService.isTenMinsToSessionStart(
            this.getSessionTime(this.sessionToShow),
            this.currentTime,
        )
        const sessionHasNotEnded = !this.utilsService.hasSessionEnded(
            this.getSessionTime(this.sessionToShow),
            this.getSessionDuration(this.sessionToShow),
            this.currentTime,
        )

        this.mobileShowUpcomingSession = sessionHasNotEnded && withinTenMinutesofSession
        if (this.mobileShowUpcomingSession) {
            this.upcomingSessionsService.setMobileUpcomingSessionVisible()
        } else {
            this.upcomingSessionsService.clearMobileUpcomingSessionVisible()
        }
    }

    private processSessions(): void {
        this.processBookings()
    }

    private processBookings(): void {
        if (!this.timeZone) {
            return
        }
        this.resetUpcomingSessionInfo()

        const upcomingSession = this.getBookingSessionToDisplay(this.meetingsBookingsMeetings)

        if (!upcomingSession) {
            return
        }

        this.sessionToShow =
            upcomingSession.meetingType === 'group_meeting'
                ? this.hydrateGroupMeetingParticipants(upcomingSession)
                : upcomingSession
        this.currentTime = this.utilsService.getCurrentTimestamp()

        this.updateMobileUpcomingSessionBanner()

        if (upcomingSession.meetingType === 'paired') {
            if (
                !!upcomingSession.partnerId &&
                this.utilsService.isTenMinsToSessionStart(upcomingSession.startMs, this.currentTime)
            ) {
                this.showTimer = true
            } else {
                this.showTimer = false
                if (this.utilsService.isSessionToday(upcomingSession.startMs, this.timeZone)) {
                    this.sessionDateStr = 'Today'
                } else {
                    this.sessionDateStr = moment(upcomingSession.startMs)
                        .tz(this.timeZone)
                        .format('dddd, MMM D')
                }
            }
        } else {
            if (
                this.utilsService.isTenMinsToSessionStart(upcomingSession.startMs, this.currentTime)
            ) {
                this.showTimer = true
            } else {
                this.showTimer = false
                if (this.utilsService.isSessionToday(upcomingSession.startMs, this.timeZone)) {
                    this.sessionDateStr = 'Today'
                } else {
                    this.sessionDateStr = moment(upcomingSession.startMs)
                        .tz(this.timeZone)
                        .format('dddd, MMM D')
                }
            }
        }

        if (
            this.utilsService.ongoingSession(
                upcomingSession.startMs,
                upcomingSession.durationMs,
                this.currentTime,
            )
        ) {
            this.isSessionOngoing = true
        } else {
            this.isSessionOngoing = false
        }
    }

    private registerForSessionUpdates(): void {
        this.meetingsService.meetingsBookingsSubject
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((eventsBookingsState) => {
                this.meetingsBookingsMeetings = eventsBookingsState.meetings
                this.meetingsParticipants = eventsBookingsState.participants ?? {}
                this.processBookings()
            })
    }

    private getBookingSessionToDisplay(
        meetings: MeetingsBookingsMeetingItem[],
    ): MeetingsBookingsMeetingItem | null {
        const currentTime = this.utilsService.getCurrentTimestamp()
        let index = meetings.findIndex((meeting) => {
            return !this.utilsService.hasSessionEnded(
                meeting.startMs,
                meeting.durationMs,
                currentTime,
            )
        })

        if (index === -1) {
            return null
        }

        for (let i = index; i < meetings.length; i++) {
            const meeting = meetings[i]
            if (meeting.meetingType === 'group_meeting') {
                return meeting
            }
            if (
                this.utilsService.shouldDisplaySession(
                    meeting.sessionType ?? 'standard',
                    meeting.startMs,
                    meeting.state ?? 0,
                    !!meeting.partnerId,
                )
            ) {
                return meeting
            }
        }

        return null
    }

    private hydrateGroupMeetingParticipants(
        groupMeeting: MeetingsBookingsGroupMeeting,
    ): MeetingsBookingsGroupMeeting {
        const participantConfigs = Array.isArray(groupMeeting.participantConfigs)
            ? groupMeeting.participantConfigs
            : []

        return {
            ...groupMeeting,
            participantConfigs,
            participants: participantConfigs.map((participant) =>
                this.resolveParticipantInfo(participant.id),
            ),
        } as MeetingsBookingsGroupMeeting
    }

    private resolveParticipantInfo(participantId: string): MeetingsParticipantInfo {
        return (
            this.meetingsParticipants?.[participantId] ??
            this.buildFallbackParticipantInfo(participantId)
        )
    }

    private buildFallbackParticipantInfo(participantId: string): MeetingsParticipantInfo {
        return {
            id: participantId,
            displayName: null,
            profileUrl: null,
            photo: null,
            saved: false,
            status: 0,
            timezone: null,
            group: null,
            completedSessions: null,
            snoozed: false,
            snoozeExpiresAt: null,
            sharedAvailability: false,
        }
    }

    private isPairedMeeting(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): session is MeetingsBookingsPairedMeeting {
        return (session as MeetingsBookingsMeetingItem).meetingType === 'paired'
    }

    public isGroupMeeting(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): session is MeetingsBookingsGroupMeeting {
        return (session as MeetingsBookingsMeetingItem).meetingType === 'group_meeting'
    }

    public getSessionTime(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): number {
        if (this.isPairedMeeting(session)) {
            return session.startMs
        }
        if (this.isGroupMeeting(session)) {
            return session.startMs
        }
        return session.sessionTime
    }

    public getSessionDuration(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): number {
        if (this.isPairedMeeting(session)) {
            return session.durationMs
        }
        if (this.isGroupMeeting(session)) {
            return session.durationMs
        }
        return session.duration
    }

    public getSessionType(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): string {
        if (this.isPairedMeeting(session)) {
            return session.sessionType ?? 'standard'
        }
        if (this.isGroupMeeting(session)) {
            return 'standard'
        }
        return session.sessionType
    }

    private getPartnerDisplayName(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): string {
        if (this.isPairedMeeting(session)) {
            const partner = this.meetingsParticipants?.[session.partnerId ?? '']
            return partner?.displayName ?? ''
        }
        if (this.isGroupMeeting(session)) {
            return ''
        }
        return session.partnerDisplayName ?? ''
    }

    public getSessionPartnerInfo(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): MeetingsParticipantInfo | null {
        if (!this.isPairedMeeting(session)) {
            return null
        }
        if (!session.partnerId) {
            return null
        }
        return this.meetingsParticipants?.[session.partnerId] ?? null
    }

    public getAmbassadorMatch(
        session: ConfirmedSessionsResponse | MeetingsBookingsMeetingItem,
    ): boolean {
        if (this.isPairedMeeting(session)) {
            return session.ambassadorMatch ?? false
        }
        if (this.isGroupMeeting(session)) {
            return false
        }
        return session.ambassadorMatch
    }

    onClickSessionBanner(): void {
        if (this.router.url && this.router.url.includes('dashboard')) {
            this.calendarViewSerivce.triggerTodaySelected()
        } else {
            this.router.navigate(['/dashboard'])
        }
    }

    onClickAmbassadorMatchInfo(): void {
        this.analyticsService.logClickedEvent(
            'Learn more',
            'Upcoming Session Panel: Ambassador Match Info',
        )
        this.dialogService.openAmbassadorMatchInfoDialog(
            this.isNewUserForAmbassadorProgram,
            this.getPartnerDisplayName(this.sessionToShow),
        )
    }

    ngOnDestroy() {
        this.upcomingSessionsService.clearMobileUpcomingSessionVisible()
        this.ngUnsubscribe.next(null)
        this.ngUnsubscribe.complete()
    }
}
